<template>
  <nav
    id="header"
    class="fixed top-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-b border-gray-100 shadow-sm"
  >
    <div
      class="max-w-[1600px] mx-auto flex items-center justify-between h-16 px-4 sm:px-6 lg:px-10"
    >
      <!-- Brand -->
      <router-link
        class="flex items-center gap-3 flex-shrink-0"
        :to="{ name: 'Dashboard' }"
      >
        <img src="/dbi-logo.svg" alt="Deep Breath Intelligence" class="h-8" />
        <span
          class="hidden sm:inline text-brand-600 font-bold text-base border-l border-gray-200 pl-3"
          >Research-App</span
        >
      </router-link>

      <!-- Context badges (project / patient) -->
      <div class="hidden md:flex items-center gap-2 ml-4 mr-auto">
        <span v-if="projectSlug" class="pill pill-brand">
          <font-awesome-icon icon="wind" />
          {{ projectName }}
        </span>
        <span v-if="patientId" class="pill pill-muted">
          <font-awesome-icon icon="user" />
          {{ patientId }}
        </span>
      </div>

      <!-- Desktop user + logout -->
      <div class="hidden lg:flex items-center gap-3 flex-shrink-0">
        <div v-if="user" class="flex items-center gap-2">
          <span
            class="w-9 h-9 rounded-full bg-brand-600 text-white text-sm font-bold flex items-center justify-center select-none"
            >{{ initials }}</span
          >
          <span class="text-sm text-ink-soft font-medium">{{
            user.email
          }}</span>
        </div>
        <router-link class="btn btn-secondary btn-sm" :to="{ name: 'Logout' }">
          <font-awesome-icon icon="sign-out-alt" />
          <span>Log out</span>
        </router-link>
      </div>

      <!-- Mobile: avatar + hamburger -->
      <div class="flex lg:hidden items-center gap-2">
        <span
          v-if="user"
          class="w-9 h-9 rounded-full bg-brand-600 text-white text-sm font-bold flex items-center justify-center select-none"
          >{{ initials }}</span
        >
        <button
          class="w-10 h-10 flex items-center justify-center rounded-xl text-ink-soft hover:bg-gray-100 focus:outline-none transition-colors"
          :aria-expanded="menuOpen ? 'true' : 'false'"
          aria-label="Toggle menu"
          @click="menuOpen = !menuOpen"
        >
          <font-awesome-icon
            :icon="menuOpen ? 'times' : 'bars'"
            class="text-lg"
          />
        </button>
      </div>
    </div>

    <!-- Mobile dropdown panel -->
    <transition name="slide-fade">
      <div
        v-if="menuOpen"
        class="lg:hidden border-t border-gray-100 bg-white px-4 sm:px-6 pb-4 pt-2 shadow-sm"
      >
        <p v-if="user" class="text-xs text-ink-muted px-2 pt-2 pb-3 truncate">
          Signed in as
          <span class="font-semibold text-ink-soft">{{ user.email }}</span>
        </p>
        <div class="space-y-1">
          <router-link
            v-for="link in links"
            :key="link.title"
            :to="{ name: link.routeName }"
            exact-active-class="is-active"
            class="mobile-link flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-ink-soft hover:bg-gray-50 transition-colors"
            @click.native="menuOpen = false"
          >
            <font-awesome-icon
              :icon="link.icon"
              class="w-4 text-center text-ink-muted"
            />
            <span>{{ link.title }}</span>
          </router-link>
        </div>
      </div>
    </transition>
  </nav>
</template>

<script>
import { mapGetters } from 'vuex'
import { getterTypes } from 'store/auth'

export default {
  data() {
    return {
      menuOpen: false,
    }
  },
  computed: {
    ...mapGetters({
      user: getterTypes.currentUser,
      isAdmin: getterTypes.isAdmin,
    }),
    links() {
      const base = [
        { title: 'Projects', routeName: 'Dashboard', icon: 'folder-open' },
        {
          title: 'Data management',
          routeName: 'DataManagement',
          icon: 'tasks',
        },
      ]
      if (this.isAdmin) {
        base.push(
          { title: 'Users', routeName: 'UserManagement', icon: 'user' },
          { title: 'Audit log', routeName: 'AuditLog', icon: 'history' }
        )
      }
      base.push(
        { title: 'Export data', routeName: 'ExportData', icon: 'upload' },
        { title: 'Support', routeName: 'Support', icon: 'info-circle' },
        { title: 'Log out', routeName: 'Logout', icon: 'sign-out-alt' }
      )
      return base
    },
    initials() {
      const email = (this.user && this.user.email) || ''
      return email.slice(0, 2).toUpperCase() || '·'
    },
    projectSlug() {
      return this.$route.params.projectSlug
    },
    patientId() {
      return this.$route.params.patientId
    },
    projectName() {
      const slug = (this.projectSlug || '').toLowerCase()
      if (slug === 'bex') return 'BigExBrEss'
      if (slug === 'bro') return 'BrOSA'
      if (slug === 'trx') return 'TripleX'
      return this.projectSlug ? this.projectSlug.toUpperCase() : ''
    },
  },
  watch: {
    // Close the mobile menu whenever the route changes.
    $route() {
      this.menuOpen = false
    },
  },
}
</script>

<style scoped>
.mobile-link.is-active {
  background-color: #fbf4fb;
  color: #831f82;
}
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.2s ease;
}
.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
